/**
 * ChannelMix — W55 "Channel Mix · MQLs This Quarter"
 * W57: rewired to query mql_events via /api/marketing/channel-mix instead of marketing_events proxy.
 *
 * Horizontal bar chart. Channels: LinkedIn, Webinar, Content syndication,
 * Roundtable, Inbound web — sourced from mql_events.source field.
 * Top channel highlighted in red (#DC2626).
 *
 * Tenant 1 only.
 */

import { useEffect, useState } from "react";
import { api, getCurrentTenant } from "@/lib/session";

const TENANT_ID = 1;

interface ChannelRow {
  source: string;
  count: number;
}

export function ChannelMix() {
  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const tenant = getCurrentTenant();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api<{ channels: ChannelRow[] }>("/api/marketing/channel-mix")
      .then((resp) => {
        if (cancelled) return;
        setChannels(resp?.channels ?? []);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setChannels([]);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [tenant?.tenantId]);

  if (loading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-4 animate-pulse">
        <div className="h-4 w-40 bg-neutral-100 rounded mb-4" />
        {[1,2,3,4,5].map(i => <div key={i} className="h-7 w-full bg-neutral-100 rounded mb-2" />)}
      </div>
    );
  }

  const maxCount = Math.max(...channels.map(c => c.count), 1);
  const topSource = channels[0]?.source;

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-500">
        Channel Mix &middot; MQLs This Quarter
      </div>
      <p className="mb-4 text-[11px] text-neutral-400">MQL count by acquisition channel from mql_events.</p>

      <div className="space-y-2">
        {channels.map(ch => {
          const barPct = maxCount > 0 ? (ch.count / maxCount) * 100 : 0;
          const isTop = ch.source === topSource && ch.count > 0;

          return (
            <div key={ch.source} className="flex items-center gap-3">
              <div className="w-36 shrink-0 text-[12px] text-neutral-700 truncate">{ch.source}</div>
              <div className="flex-1 relative h-6 rounded bg-neutral-100 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded transition-all duration-500"
                  style={{
                    width: `${barPct}%`,
                    backgroundColor: isTop ? "#DC2626" : "#0B0B0F",
                    opacity: 0.85,
                  }}
                />
              </div>
              <div
                className="w-8 shrink-0 text-right text-[12px] font-semibold tabular-nums"
                style={{ color: isTop ? "#DC2626" : "#0B0B0F" }}
              >
                {ch.count}
              </div>
            </div>
          );
        })}
      </div>

      {channels.every(c => c.count === 0) && (
        <p className="mt-2 text-[11px] text-neutral-400">
          No MQL events found for the current quarter.
        </p>
      )}
    </div>
  );
}
